import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, RotateCcw, Volume2, Star } from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';
import { playZippieSound } from '@/lib/zippieSound';

const DragDropGame = ({ activity, onComplete, childId }) => {
  const [items, setItems] = useState([]);
  const [dropZones, setDropZones] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    initializeGame();
  }, [activity]);

  const initializeGame = () => {
    const content = activity.content || {};
    
    // Handle different game types
    if (activity.id?.includes('noah')) {
      // Noah's Ark - match animals
      const animals = content.pairs || [
        { animal: 'Lion', count: 2 },
        { animal: 'Elephant', count: 2 },
        { animal: 'Giraffe', count: 2 },
      ];
      const allAnimals = animals.flatMap(a => 
        Array(a.count).fill(null).map((_, i) => ({
          id: `${a.animal}-${i}`,
          label: a.animal,
          matched: false
        }))
      );
      setItems(shuffleArray(allAnimals));
      setDropZones([{ id: 'ark', label: 'The Ark', items: [] }]);
    } else if (activity.id?.includes('creation')) {
      // 7 Days of Creation - match days
      const days = content.days || [];
      setItems(shuffleArray(days.map(d => ({
        id: `day-${d.day}`,
        label: d.creation,
        day: d.day,
        matched: false
      }))));
      setDropZones(days.map(d => ({
        id: `zone-${d.day}`,
        label: `Day ${d.day}`,
        correctItem: `day-${d.day}`,
        item: null
      })));
    } else if (activity.id?.includes('abc-order') || activity.id?.includes('alphabets')) {
      // ABC Order
      const letters = content.scrambled || content.letters || ['C', 'A', 'E', 'B', 'D'];
      const correctOrder = content.letters || [...letters].sort();
      setItems(letters.map((l, i) => ({
        id: `letter-${l}-${i}`,
        label: l,
        order: correctOrder.indexOf(l),
        matched: false
      })));
      setDropZones(correctOrder.map((l, i) => ({
        id: `slot-${i}`,
        label: `${i + 1}`,
        correctItem: `letter-${l}`,
        item: null
      })));
    } else if (activity.id?.includes('number-line') || activity.id?.includes('counting')) {
      // Number Line
      const numbers = content.scrambled || content.numbers || [3, 1, 5, 2, 4];
      const correctOrder = content.numbers || [...numbers].sort((a, b) => a - b);
      setItems(numbers.map((n, i) => ({
        id: `num-${n}-${i}`,
        label: n.toString(),
        num: n,
        matched: false
      })));
      setDropZones(correctOrder.map((n, i) => ({
        id: `numslot-${i}`,
        label: `${i + 1}`,
        correctNum: n,
        item: null
      })));
    } else if (activity.id?.includes('fruits-spirit')) {
      // Fruits of the Spirit
      const fruits = content.fruits || ['Love', 'Joy', 'Peace', 'Patience'];
      const matching = content.matching || [];
      setItems(shuffleArray(fruits.map(f => ({
        id: `fruit-${f}`,
        label: f,
        matched: false
      }))));
      setDropZones(matching.map(m => ({
        id: `example-${m.fruit}`,
        label: m.example,
        correctFruit: m.fruit,
        item: null
      })));
    } else if (activity.id?.includes('armor')) {
      // Armor of God
      const pieces = content.armor_pieces || [];
      setItems(shuffleArray(pieces.map(p => ({
        id: `armor-${p.piece}`,
        label: p.piece,
        bodyPart: p.body_part,
        matched: false
      }))));
      setDropZones(pieces.map(p => ({
        id: `body-${p.body_part}`,
        label: p.body_part.charAt(0).toUpperCase() + p.body_part.slice(1),
        correctArmor: p.piece,
        item: null
      })));
    } else if (activity.id?.includes('food-chain')) {
      // Food Chain
      const chain = content.chain || ['Sun', 'Grass', 'Rabbit', 'Fox'];
      setItems(shuffleArray(chain.map((c, i) => ({
        id: `chain-${c}`,
        label: c,
        order: i,
        matched: false
      }))));
      setDropZones(chain.map((c, i) => ({
        id: `chainslot-${i}`,
        label: `Step ${i + 1}`,
        correctItem: c,
        item: null
      })));
    } else {
      // Default matching game
      const pairs = content.pairs || content.shapes || [
        { word1: 'cat', word2: 'hat' },
        { word1: 'dog', word2: 'log' },
      ];
      setItems(shuffleArray(pairs.flatMap(p => [
        { id: `w1-${p.word1 || p.shape}`, label: p.word1 || p.shape || p.name, matched: false },
        { id: `w2-${p.word2 || p.name}`, label: p.word2 || p.name, matched: false },
      ])));
      setDropZones([]);
    }
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleDragStart = (item) => {
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDrop = (zone) => {
    if (!draggedItem || zone.item) return;
    
    setAttempts(a => a + 1);
    
    // Check if correct
    let isCorrect = false;
    
    if (zone.correctItem) {
      isCorrect = draggedItem.id.includes(zone.correctItem.replace('letter-', '').replace('day-', ''));
    } else if (zone.correctNum !== undefined) {
      isCorrect = draggedItem.num === zone.correctNum;
    } else if (zone.correctFruit) {
      isCorrect = draggedItem.label === zone.correctFruit;
    } else if (zone.correctArmor) {
      isCorrect = draggedItem.label === zone.correctArmor;
    } else if (zone.id === 'ark') {
      isCorrect = true; // Noah's ark accepts all animals
    }
    
    if (isCorrect) {
      setScore(s => s + 10);
      toast.success('Correct!', { duration: 1000 });
      try { playZippieSound(); } catch { /* no-op */ }
      
      // Update drop zone
      setDropZones(zones => zones.map(z => 
        z.id === zone.id ? { ...z, item: draggedItem } : z
      ));
      
      // Remove item from items list
      setItems(items => items.filter(i => i.id !== draggedItem.id));
      
      // Check if game complete
      const remainingItems = items.filter(i => i.id !== draggedItem.id);
      if (remainingItems.length === 0) {
        handleGameComplete();
      }
    } else {
      toast.error('Try again!', { duration: 1000 });
    }
    
    setDraggedItem(null);
  };

  const handleGameComplete = () => {
    setCompleted(true);
    const finalScore = Math.round((score / (attempts || 1)) * 100);
    toast.success(`Great job! Score: ${finalScore}%`);
    
    if (onComplete) {
      onComplete(finalScore, attempts);
    }
  };

  const handleReset = () => {
    setCompleted(false);
    setScore(0);
    setAttempts(0);
    initializeGame();
  };

  const speakText = (text) => {
    _speakTTS(text, 0.85);
  };

  return (
    <div className="space-y-6" data-testid="drag-drop-game">
      {/* Instructions */}
      <Card className="bg-gradient-to-r from-sky-50 to-blue-50 border-sky-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-sky-800 font-medium">{activity.instructions}</p>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => speakText(activity.instructions)}
              className="text-sky-600"
              data-testid="speak-instructions-btn"
            >
              <Volume2 className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Score Display */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-yellow-600">
          <Star className="w-5 h-5" />
          <span className="font-bold">Score: {score}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="gap-2"
          data-testid="reset-game-btn"
        >
          <RotateCcw className="w-4 h-4" /> Reset
        </Button>
      </div>

      {/* Drop Zones */}
      {dropZones.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
          {dropZones.map((zone) => (
            <motion.div
              key={zone.id}
              className={`min-h-24 rounded-xl border-3 border-dashed transition-all ${
                zone.item 
                  ? 'border-green-400 bg-green-50' 
                  : draggedItem 
                    ? 'border-purple-400 bg-purple-50' 
                    : 'border-slate-300 bg-slate-50'
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(zone)}
              data-testid={`drop-zone-${zone.id}`}
            >
              <div className="p-3 text-center h-full flex flex-col justify-center">
                <p className="text-xs text-slate-500 mb-1">{zone.label}</p>
                {zone.item ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="bg-green-500 text-white px-3 py-2 rounded-lg font-bold"
                  >
                    {zone.item.label}
                    <CheckCircle className="w-4 h-4 inline ml-2" />
                  </motion.div>
                ) : (
                  <div className="text-slate-400 text-sm">Drop here</div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Draggable Items */}
      <div className="flex flex-wrap gap-3 justify-center">
        <AnimatePresence>
          {items.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              draggable
              onDragStart={() => handleDragStart(item)}
              onDragEnd={handleDragEnd}
              className={`px-4 py-3 rounded-xl font-bold cursor-grab active:cursor-grabbing select-none transition-all ${
                draggedItem?.id === item.id
                  ? 'bg-purple-500 text-white scale-110 shadow-lg'
                  : 'bg-gradient-to-br from-purple-400 to-pink-400 text-white hover:shadow-md'
              }`}
              onClick={() => speakText(item.label)}
              data-testid={`drag-item-${item.id}`}
            >
              {item.label}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Completion Message */}
      {completed && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center">
            <Star className="w-10 h-10 text-white" />
          </div>
          <h3 className="font-fredoka text-2xl font-bold text-purple-800 mb-2">Amazing Job!</h3>
          <p className="text-purple-600 mb-4">You completed the activity!</p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={handleReset}
              variant="outline"
              className="gap-2"
              data-testid="play-again-btn"
            >
              <RotateCcw className="w-4 h-4" /> Play Again
            </Button>
          </div>
        </motion.div>
      )}

      {/* Hints */}
      {activity.hints && activity.hints.length > 0 && !completed && (
        <div className="text-center">
          <p className="text-sm text-slate-500">
            Hint: {activity.hints[0]}
          </p>
        </div>
      )}
    </div>
  );
};

export default DragDropGame;
